import { Form, FormikProvider, useFormik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button, Spacing, Stack } from "@noom/wax-component-library";

import { InputWithSuffix } from "@/components/form";
import { useTrackEvent } from "@/hooks";
import {
  shouldAskAccessWaistCircumference,
  WAIST_CIRCUMFERENCE_MAX_INCHES,
  WAIST_CIRCUMFERENCE_MIN_INCHES,
} from "@/utils/cmsAccess";
import { isCmsAccessEnabled } from "@/utils/partners";

import { QuestionComponentProps, QuestionDefinition } from "../../Question";
import { BaseQuestion } from "../core";

const QUESTION_ID = "waistCircumference";

declare module "@/contexts" {
  interface SurveyAnswers {
    // In inches, as entered by the member.
    waistCircumference?: number;
  }
}

type FormValues = {
  // Kept as a string so the input shows exactly what the member typed.
  waistCircumference: string;
};

// Digits with at most one period and one digit after it, e.g. "34" or "34.5".
const ALLOWED_INPUT = /^\d*\.?\d?$/;

const trimLeadingZeros = (value: string) => value.replace(/^0+(?=\d)/, "");

const WaistCircumferenceQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { t } = useTranslation();
  const { trackAnonEvent } = useTrackEvent();

  const questionText = t(`${QUESTION_ID}.questionText`);
  const invalidMessage = t(`${QUESTION_ID}.errors.invalid`);

  const formik = useFormik<FormValues>({
    initialValues: {
      waistCircumference: surveyAnswers[QUESTION_ID]?.toString() ?? "",
    },
    onSubmit: ({ waistCircumference }) =>
      onClickNext(Number(waistCircumference)),
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: Yup.object({
      waistCircumference: Yup.number()
        .typeError(invalidMessage)
        .required(invalidMessage)
        .min(WAIST_CIRCUMFERENCE_MIN_INCHES, invalidMessage)
        .max(WAIST_CIRCUMFERENCE_MAX_INCHES, invalidMessage),
    }),
  });

  // Anything that is not a number with up to one decimal place is blocked as
  // the member types.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (ALLOWED_INPUT.test(e.target.value)) {
      formik.setFieldValue(QUESTION_ID, e.target.value);
    }
  };

  const handleBlur = () => {
    formik.setFieldValue(
      QUESTION_ID,
      trimLeadingZeros(formik.values.waistCircumference),
    );
  };

  const handleClickContinue = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "Continue",
      pageId: QUESTION_ID,
    });
  };

  return (
    <BaseQuestion
      onClickBack={onClickBack}
      questionHelperText={t(`${QUESTION_ID}.helperText`)}
      questionId={QUESTION_ID}
      questionText={questionText}
    >
      <FormikProvider value={formik}>
        <Form>
          <Stack spacing={Spacing[8]}>
            <InputWithSuffix
              aria-label={questionText}
              autoFocus
              error={formik.errors.waistCircumference}
              inputMode="decimal"
              name={QUESTION_ID}
              onBlur={handleBlur}
              onChange={handleChange}
              suffix={t(`${QUESTION_ID}.unit`)}
              type="text"
              value={formik.values.waistCircumference}
            />
            <Button
              colorScheme="primary"
              disabled={!formik.values.waistCircumference}
              onClick={handleClickContinue}
              size="xl"
              type="submit"
            >
              {t(`${QUESTION_ID}.ctaText`)}
            </Button>
          </Stack>
        </Form>
      </FormikProvider>
    </BaseQuestion>
  );
};

export const Q_ACCESS_WAIST_CIRCUMFERENCE: QuestionDefinition = {
  id: QUESTION_ID,
  // Only ACCESS members with an overweight BMI need the waist circumference.
  shouldShowQuestion: ({ partnerInfo, surveyAnswers }) =>
    isCmsAccessEnabled(partnerInfo) &&
    shouldAskAccessWaistCircumference(surveyAnswers.heightWeight),
  component: WaistCircumferenceQuestion,
};
